import React, { useEffect, useRef, useState } from "react";

/**
 * CeipalJobsWidget — embed the CEIPAL job board widget safely in React.
 *
 * Used ONLY on /careers/jobs (corporate careers page).
 * NEVER imported by /shiftbridge/* (the PRN marketplace is separate).
 *
 * Behavior:
 *  - Loads CEIPAL widget.js once after mount (no duplicate injection).
 *  - Targets <div id="example-widget-container"> exactly as the CEIPAL docs expect.
 *  - Shows a loading message until the script tag finishes.
 *  - Surfaces a graceful error message if the script fails to load.
 *  - No SMTP/server secrets — pure client-side widget driven by CEIPAL's public API key.
 */

const CEIPAL_SCRIPT_SRC = "https://jobsapi.ceipal.com/APISource/widget.js";
const CEIPAL_API_KEY    = "SEl3NUNoSEJUTlpmWktQN1lmVjJvdz09";
const CEIPAL_PORTAL_ID  = "UGtpQkJSTEZ3Z0xBaDdsN1QwOXBIUT09";

export default function CeipalJobsWidget() {
  const [scriptState, setScriptState] = useState("loading"); // "loading" | "loaded" | "error"
  const injectedRef = useRef(false);

  useEffect(() => {
    if (typeof window === "undefined") return undefined;

    // Idempotent inject — never add a second copy if user navigates back
    const existing = document.querySelector(
      `script[src="${CEIPAL_SCRIPT_SRC}"]`
    );
    if (existing) {
      injectedRef.current = true;
      setScriptState("loaded");
      return undefined;
    }

    const s = document.createElement("script");
    s.src   = CEIPAL_SCRIPT_SRC;
    s.async = true;
    s.setAttribute("data-ceipal-api-key", CEIPAL_API_KEY);
    s.setAttribute("data-ceipal-career-portal-id", CEIPAL_PORTAL_ID);
    s.onload  = () => setScriptState("loaded");
    s.onerror = () => setScriptState("error");
    document.body.appendChild(s);
    injectedRef.current = true;

    // Intentionally do NOT remove the script on unmount — CEIPAL re-uses
    // its own injected DOM nodes. Removing breaks re-mount on route change.
    return undefined;
  }, []);

  return (
    <div className="w-full" data-testid="ceipal-widget-root">
      {scriptState === "loading" && (
        <div
          className="rounded-2xl border border-slate-200 bg-white px-6 py-12 text-center shadow-card"
          data-testid="ceipal-widget-loading"
        >
          <div className="font-display font-bold text-trital-navy text-lg">
            Loading current opportunities…
          </div>
          <p className="mt-2 text-sm text-slate-500">
            Pulling live healthcare jobs from our recruiting system.
          </p>
        </div>
      )}

      {scriptState === "error" && (
        <div
          className="rounded-2xl border border-red-200 bg-red-50 px-6 py-8 text-center"
          data-testid="ceipal-widget-error"
          role="alert"
        >
          <div className="font-display font-bold text-red-700 text-base">
            Job board temporarily unavailable.
          </div>
          <p className="mt-2 text-sm text-red-600">
            Please refresh the page or{" "}
            <a
              href="mailto:careers@tritalcare.com"
              className="underline font-semibold hover:text-red-800"
            >
              email careers@tritalcare.com
            </a>{" "}
            to reach our recruiting team directly.
          </p>
        </div>
      )}

      {/* CEIPAL injects its rendered HTML into this exact id. Keep it mounted
          even while loading so the script has its target ready. */}
      <div
        id="example-widget-container"
        className="ceipal-widget-container mt-6"
        data-testid="ceipal-widget-container"
      />
    </div>
  );
}
